import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { selectUser } from '@backrs_ui/grow-ui/src/redux/user/user';
import { useVerifyEmailMutation } from '@backrs_ui/grow-ui/src/redux/user/user.api';

import { Box, Typography, CircularProgress, SxProps, Theme, Button } from '@mui/material';

const {
  modules: { verifyEmail },
} = appStrings;

export const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const [status, setStatus] = useState<'loading' | 'error'>('loading');

  const [triggerVerifyEmail] = useVerifyEmailMutation();

  useEffect(() => {
    const queryString = searchParams.toString();

    if (!queryString) {
      setStatus('error');

      return;
    }

    triggerVerifyEmail(queryString)
      .unwrap()
      .then(() => {
        navigate(user ? APP_ROUTE.HOME : APP_ROUTE.LOGIN);
      })
      .catch(() => {
        setStatus('error');
      });
  }, [searchParams, triggerVerifyEmail, navigate, user]);

  if (status === 'loading') {
    return (
      <Box sx={containerSx}>
        <Box sx={contentWrapperSx}>
          <Box sx={loadingContainerSx}>
            <CircularProgress size={24} />
            <Typography variant="text-md-bold">{verifyEmail.loading}</Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (status === 'error') {
    return (
      <Box sx={containerSx}>
        <Box sx={contentWrapperSx}>
          <Box sx={{ mb: ({ backrs }) => backrs.spacing['spacing-3xl'] }}>
            <Typography variant="text-lg-semibold">{verifyEmail.error.title}</Typography>
            <Typography variant="text-md-medium">{verifyEmail.error.subtitle}</Typography>
          </Box>
          <Button
            hierarchy="Secondary gray"
            size="lg"
            onClick={() => window.open('mailto:support@backrs.com', '_blank')}
          >
            {verifyEmail.error.contactSupport}
          </Button>
        </Box>
      </Box>
    );
  }

  return null;
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  height: '100vh',
  width: '100vw',
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: backrs.spacing['spacing-xl'],
});

const contentWrapperSx: SxProps<Theme> = {
  maxWidth: '500px',
  width: '100%',
  textAlign: 'center',
};

const loadingContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xl'],
});
